import { createContext, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import { ExpandedState } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useModal } from 'hooks/useModal';
import { DoubleScrollbar } from 'components/DoubleScrollbar';
import ErrorMessage from 'components/ErrorMessage/index';
import { adaptLegacyColumns } from './adapters/legacyColumnAdapter';
import { useColumnPersistence } from './hooks/useColumnPersistence';
import { useClientFilter } from './hooks/useClientFilter';
import { useGhostHeader } from './hooks/useGhostHeader';
import { useTAccountView } from './hooks/useTAccountView';
import { useTableInstance } from './hooks/useTableInstance';
import { useShowColumnKeys } from './hooks/useShowColumnKeys';
import { TableHeader } from './components/TableHeader';
import { TableBody } from './components/TableBody';
import { TableFooter } from './components/TableFooter';
import { ColumnSelectorModal } from './components/ColumnSelectorModal';
import { ClientFilterModal } from './components/ClientFilterModal';
import { ActiveFilterChips } from './components/ActiveFilterChips';
import { TAccount } from './TAccount';
import { TAccountFilterChips } from './TAccount/TAccountFilterChips';
import { TableProps } from './types';
import styles from './styles.module.scss';

const cx = classNames.bind(styles);

const TableNestingContext = createContext(false);

// Stable reference so an omitted `rows` doesn't hand out a new array each render, which would
// otherwise re-fire onFilteredRowsChange and loop a caller wiring it into setState.
const EMPTY_ROWS: never[] = [];

export const Table = <TData extends object>({
  rows = EMPTY_ROWS,
  columns,
  rowIdKey = 'id',
  expandedContent,
  actions,
  noResultMessage,
  sortConfig,
  tableKey,
  highlightedRow,
  header = true,
  sumable = false,
  TAccountView = false,
  stickyHeader = true,
  rightColumnButton,
  onColumnsChange,
  onFilteredRowsChange,
}: TableProps<TData>) => {
  const intl = useIntl();
  const { isModalOpen, openModal, closeModal } = useModal();
  const isNestedTable = useContext(TableNestingContext);
  const effectiveStickyHeader = stickyHeader && !isNestedTable;
  // No heuristic fallback on purpose: several real pages render sibling tables, so persistence
  // requires an explicit tableKey.
  const canPersist = Boolean(tableKey);

  const isExpandable = Boolean(expandedContent);

  const [expanded, setExpanded] = useState<ExpandedState>({});

  // Column persistence (visibility + order)
  const { columnVisibility, setColumnVisibility, columnOrder, setColumnOrder, isDefault, resetToDefaults } =
    useColumnPersistence(columns, tableKey ?? undefined, onColumnsChange ?? undefined);

  // T-account view
  const [isTAccountViewEnabled] = useTAccountView(Boolean(TAccountView));

  // Ctrl/Cmd + Alt + T toggles raw column keys instead of translated names
  const showColumnKeys = useShowColumnKeys();

  // Column defs — re-adapt when columns prop changes (dynamic columns)
  const columnDefs = useMemo(() => adaptLegacyColumns<TData>(columns), [columns]);

  // Client-side filter (must come before useTableInstance so columnFilters can be passed in)
  const {
    columnFilters,
    setColumnFilter,
    clearColumnFilter,
    activeFilters,
    filterValues,
    isFilterModalOpen,
    filterModalColumn,
    openFilterModal,
    closeFilterModal,
    columnHasFilter,
  } = useClientFilter(tableKey ?? undefined, canPersist);

  // TanStack table instance
  const table = useTableInstance<TData>({
    rows: rows ?? [],
    columnDefs,
    columnVisibility,
    columnOrder,
    expanded,
    setExpanded,
    columnFilters,
    sortConfig,
    rowIdKey,
  });

  // Recompute whenever filter state or server data changes (table ref is stable).
  const filteredOriginalRows = useMemo(
    () => table.getFilteredRowModel().rows.map((r) => r.original),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnFilters, rows]
  );

  // useLayoutEffect so a session-restored filter never leaves a stale-unfiltered paint; compare
  // by content, not reference, to avoid a feedback loop from an unmemoized `columns` upstream.
  const lastEmittedRowsRef = useRef<TData[] | null>(null);
  useLayoutEffect(() => {
    const last = lastEmittedRowsRef.current;
    const unchanged =
      last !== null &&
      last.length === filteredOriginalRows.length &&
      last.every((row, i) => row === filteredOriginalRows[i]);
    if (unchanged) return;
    lastEmittedRowsRef.current = filteredOriginalRows;
    onFilteredRowsChange?.(filteredOriginalRows);
  }, [filteredOriginalRows, onFilteredRowsChange]);

  const {
    realTableRef,
    ghostScrollRef,
    contentContainerRef,
    stickyScrollbarRef,
    ghostVisible,
    colWidths,
    realTableWidth,
    ghostLeft,
    ghostWidth,
    handleTableContainerScroll,
    handleStickyScrollbarScroll,
  } = useGhostHeader({
    enabled: effectiveStickyHeader,
    hasData: (rows?.length ?? 0) > 0,
    columnOrder,
    isTAccountViewEnabled,
  });

  if (!rows || rows.length < 1) {
    return (
      <ErrorMessage variant="info" value={noResultMessage || intl.formatMessage({ id: 'warnings.noData' })} small />
    );
  }

  const tableHeaderProps = {
    table,
    sortConfig,
    expandable: isExpandable,
    hasActions: Boolean(actions?.length),
    hasColumnSelector: Boolean(tableKey),
    isDefault,
    openColumnModal: openModal,
    clientFilter: true,
    onOpenFilterModal: openFilterModal,
    columnHasFilter,
    columnOrder,
    onColumnOrderChange: setColumnOrder,
    rightColumnButton,
    filterValues,
    showColumnKeys,
  };

  const tableBodyProps = {
    table,
    expandable: isExpandable,
    expandedContent,
    actions,
    highlightedRow,
    hasColumnSelector: Boolean(tableKey),
    tableKey,
    canPersist,
  };

  return (
    <TableNestingContext.Provider value={true}>
      {tableKey && (
        <ColumnSelectorModal
          key={String(isModalOpen)}
          isOpen={isModalOpen}
          onClose={closeModal}
          columns={columns}
          columnVisibility={columnVisibility}
          showColumnKeys={showColumnKeys}
          onSave={(vis) => {
            setColumnVisibility(vis);
            closeModal();
          }}
          onReset={() => {
            resetToDefaults();
            closeModal();
          }}
        />
      )}

      {filterModalColumn && (
        <ClientFilterModal
          isOpen={isFilterModalOpen}
          onClose={closeFilterModal}
          column={filterModalColumn}
          allRows={rows}
          currentValues={(columnFilters.find((f) => f.id === filterModalColumn.key)?.value as unknown[]) ?? []}
          onSave={setColumnFilter}
          onReset={clearColumnFilter}
        />
      )}

      <div className={cx({ tableView: true, stickyHeader: effectiveStickyHeader })}>
        <ActiveFilterChips
          activeFilters={activeFilters}
          columns={columns}
          allRows={rows}
          clearColumnFilter={clearColumnFilter}
          filteredCount={filteredOriginalRows.length}
          totalCount={rows.length}
        />

        {isTAccountViewEnabled ? (
          <TAccount rows={table.getFilteredRowModel().rows.map((r) => r.original)} stickyHeader={effectiveStickyHeader}>
            <TAccountFilterChips
              columns={columns}
              columnHasFilter={columnHasFilter}
              openFilterModal={openFilterModal}
            />
          </TAccount>
        ) : (
          <>
            {effectiveStickyHeader && header && (
              <div
                className={styles.ghostHeader}
                style={{ display: ghostVisible ? 'block' : 'none', left: ghostLeft, width: ghostWidth }}
              >
                <div ref={ghostScrollRef} className={styles.ghostScroller}>
                  <table className={cx({ table: true })} style={{ tableLayout: 'fixed', width: realTableWidth }}>
                    <colgroup>
                      {colWidths.map((width, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <col key={i} style={{ width }} />
                      ))}
                    </colgroup>
                    <TableHeader {...tableHeaderProps} />
                  </table>
                </div>
              </div>
            )}
            {effectiveStickyHeader ? (
              <>
                <div
                  ref={contentContainerRef}
                  className={styles.stickyTableContainer}
                  onScroll={handleTableContainerScroll}
                >
                  <table id="tableDiv" ref={realTableRef} className={cx({ table: true })}>
                    {header && <TableHeader {...tableHeaderProps} />}
                    <TableBody {...tableBodyProps} />
                    <TableFooter table={table} sumable={sumable} expandable={isExpandable} />
                  </table>
                </div>
                <div ref={stickyScrollbarRef} className={styles.stickyScrollbar} onScroll={handleStickyScrollbarScroll}>
                  <div style={{ width: realTableWidth, height: 1 }} />
                </div>
              </>
            ) : (
              <DoubleScrollbar>
                <table id="tableDiv" ref={realTableRef} className={cx({ table: true })}>
                  {header && <TableHeader {...tableHeaderProps} />}
                  <TableBody {...tableBodyProps} />
                  <TableFooter table={table} sumable={sumable} expandable={isExpandable} />
                </table>
              </DoubleScrollbar>
            )}
          </>
        )}
      </div>
    </TableNestingContext.Provider>
  );
};

Table.displayName = 'Table';
